import { memo, useCallback, useEffect, useState } from "react";
import { Pagination } from "services/search";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { NoResults } from "ui/components/NoResults";
import { TableSearch } from "ui/components/Table/TableSearch";
import { DASHBOARD_INVOICE_SEARCH_COLUMNS } from "services/api/dashboard/consts";
import { transformRoute, useTranslate } from "helpers";
import {
  deleteFleetVehicleInvoiceAPI,
  getFleetVehicleInvoiceAPI,
  getFleetVehicleInvoicesAPI,
} from "services/api/fleet/vehicles/invoices/api";
import { InvoiceOutgoingRow } from "services/api/fleet/vehicles/invoices/types";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import { useNavigate } from "react-router-dom";
import { Dialog } from "ui/components/Dialog";

const InvoiceSaleSearchTab: React.FC = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<InvoiceOutgoingRow>
  >(initialDataWithPagination);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [invoiceId, setInvoiceId] = useState<string>("");
  const [invoiceVehicleId, setInvoiceVehicleId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (pagination: Pagination) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleInvoicesAPI(pagination);
      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    const invoice = await getFleetVehicleInvoiceAPI(id);
    if (invoice && invoice.vehicle) {
      navigate(
        transformRoute(FleetVehicleRoutes.FleetVehicleInvoiceSearchPageRouter, {
          id: invoice.vehicle.id,
        })
      );
    }
  }, [navigate]);

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    id: string,
    vehicleId: string
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setInvoiceId(id);
    setInvoiceVehicleId(vehicleId);
    setOpenDialog(true);
  };

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!invoiceId || !invoiceVehicleId) return;
    try {
      const deleted = await deleteFleetVehicleInvoiceAPI(invoiceVehicleId, invoiceId, t);
      if (deleted) {
        await fetchSearchResult(searchResults.pagination);
      }
      setOpenDialog(false);
      setInvoiceId("");
      setInvoiceVehicleId("");
    } catch (err) {
      console.error("Error deleting sale invoice:", err);
    }
  }, [invoiceId, invoiceVehicleId, t, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setInvoiceId("");
    setInvoiceVehicleId("");
    setOpenDialog(false);
  }, []);

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  const handleContactClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, contactId: string) => {
      event.preventDefault();
      event.stopPropagation();
      navigate(transformRoute(ContactRoutes.ContactDetailPageRouter, { id: contactId }));
    },
    [navigate]
  );

  return (
    <div className="rounded-md mb-4 h-full w-full">
      <div>
        {!isLoading && searchResults.pagination.totalPages === 0 ? (
          <NoResults message={t("admin.dashboard.widget.invoices.search.noresults")} />
        ) : (
          <div className="mx-[-1px] mb-[-1px]">
            <TableSearch
              columns={DASHBOARD_INVOICE_SEARCH_COLUMNS(t, handleContactClick, handleDeleteClick)}
              data={searchResults.data}
              selectedRows={[]}
              pagination={
                searchResults.pagination || initialDataWithPagination.pagination
              }
              selectableRows={false}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
      <Dialog
        open={openDialog}
        title={t("admin.fleet.vehicle.invoice.dialog.delete.title")}
        message={t("admin.fleet.vehicle.invoice.dialog.delete.message")}
        confirmText={t("admin.fleet.vehicle.invoice.dialog.delete.confirmtext")}
        cancelText={t("admin.fleet.vehicle.invoice.dialog.delete.canceltext")}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </div>
  );
};

export default memo(InvoiceSaleSearchTab);
